import React, { useState, useEffect, useCallback } from 'react';
import { policyAPI, clientSettingsAPI } from '../../../services';
import { FileCheck, Plus, Users, CheckCircle, Clock, Send, Trash2, Eye, X, Upload, RefreshCw, BarChart3 } from 'lucide-react';

const CATEGORIES = ['hr','it','finance','operations','compliance','safety','code_of_conduct','leave','other'];
const CAT_LABELS = { hr:'HR Policy', it:'IT Policy', finance:'Finance', operations:'Operations', compliance:'Compliance', safety:'Safety', code_of_conduct:'Code of Conduct', leave:'Leave Policy', other:'Other' };

const PoliciesPage = () => {
  const [policies, setPolicies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [form, setForm] = useState({ title:'', description:'', category:'hr', assignmentType:'all', targetDepartments:[], dueDate:'', isPublished:false });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('list');
  const [toast, setToast] = useState(null);
  const [departments, setDepartments] = useState([]);

  const showToast = (msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await policyAPI.getPolicies();
      const d = res?.data;
      setPolicies(Array.isArray(d) ? d : []);
    } catch { showToast('Failed to load', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadDepartments = async () => {
    try {
      const res = await clientSettingsAPI.getDepartments();
      setDepartments(Array.isArray(res?.data) ? res.data : []);
    } catch { /* ignore */ }
  };

  const openCreate = () => {
    setForm({ title:'', description:'', category:'hr', assignmentType:'all', targetDepartments:[], dueDate:'', isPublished:false });
    setFile(null); setEditId(null); setShowForm(true); loadDepartments();
  };

  const handleSave = async () => {
    if (!form.title.trim()) return showToast('Title is required', 'error');
    try {
      setSaving(true);
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (k !== 'targetDepartments' && v !== '' && v !== null && v !== undefined) fd.append(k, v); });
      fd.set('targetDepartments', JSON.stringify(Array.isArray(form.targetDepartments) ? form.targetDepartments : []));
      if (file) fd.append('file', file);
      if (editId) await policyAPI.updatePolicy(editId, fd);
      else await policyAPI.createPolicy(fd);
      showToast(editId ? 'Policy updated' : 'Policy created');
      setShowForm(false); load();
    } catch { showToast('Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const handlePublish = async (id) => {
    try { await policyAPI.publishPolicy(id); showToast('Policy published'); load(); }
    catch { showToast('Publish failed', 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this policy?')) return;
    try { await policyAPI.deletePolicy(id); showToast('Deleted'); load(); }
    catch { showToast('Delete failed', 'error'); }
  };

  const openStats = async (policy) => {
    try {
      const res = await policyAPI.getPolicyStats(policy._id);
      setStats(res?.data || null);
      setSelectedPolicy(policy);
      setTab('stats');
    } catch { showToast('Failed to load stats', 'error'); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type==='error'?'bg-red-500':'bg-green-500'}`}>{toast.msg}</div>}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><FileCheck className="w-6 h-6 text-blue-600"/>Policy Acknowledgement</h1>
          <p className="text-sm text-gray-500 mt-1">Upload policies and track employee acknowledgements</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"><RefreshCw className="w-4 h-4"/></button>
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"><Plus className="w-4 h-4"/>New Policy</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-1 w-fit">
        {[['list','All Policies'],['stats','Acknowledgements']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab===k?'bg-white shadow text-blue-600':'text-gray-600 hover:text-gray-900'}`}>{l}</button>
        ))}
      </div>

      {tab === 'list' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? <p className="col-span-3 text-center py-10 text-gray-400">Loading...</p>
          : policies.length === 0 ? (
            <div className="col-span-3 flex flex-col items-center py-16 text-gray-400 bg-white rounded-xl border border-gray-200">
              <FileCheck className="w-12 h-12 mb-3 opacity-30"/>
              <p>No policies yet. Create your first policy.</p>
            </div>
          ) : policies.map(p => (
            <div key={p._id} className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-gray-900">{p.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{CAT_LABELS[p.category]} · {p.assignmentType === 'all' ? 'All Employees' : 'By Department'}</p>
                  {p.dueDate && <p className="text-xs text-orange-600 mt-0.5">Due: {new Date(p.dueDate).toLocaleDateString('en-IN')}</p>}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${p.isPublished?'bg-green-100 text-green-700':'bg-yellow-100 text-yellow-700'}`}>{p.isPublished?'Published':'Draft'}</span>
              </div>
              {p.description && <p className="text-xs text-gray-500 line-clamp-2">{p.description}</p>}
              {p.fileName && <p className="text-xs text-blue-600">📎 {p.fileName}</p>}
              <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                <button onClick={() => openStats(p)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded" title="View acknowledgements"><BarChart3 className="w-4 h-4"/></button>
                {!p.isPublished && <button onClick={() => handlePublish(p._id)} className="p-1.5 text-green-600 hover:bg-green-50 rounded" title="Publish"><Send className="w-4 h-4"/></button>}
                <button onClick={() => { setForm({ title:p.title, description:p.description||'', category:p.category, assignmentType:p.assignmentType, targetDepartments:p.targetDepartments||[], dueDate:p.dueDate?p.dueDate.split('T')[0]:'', isPublished:p.isPublished }); setEditId(p._id); setFile(null); setShowForm(true); loadDepartments(); }} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded ml-auto"><Eye className="w-4 h-4"/></button>
                <button onClick={() => handleDelete(p._id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4"/></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'stats' && stats && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <p className="font-semibold text-gray-900">{selectedPolicy?.title} — Acknowledgements</p>
            <div className="flex gap-3 text-sm">
              <span className="text-green-700 font-semibold">{stats.ackCount} acknowledged</span>
              <span className="text-red-600 font-semibold">{stats.pending?.length} pending</span>
              <span className="text-gray-500">of {stats.total}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 divide-x divide-gray-100">
            <div>
              <p className="text-xs font-semibold text-green-700 bg-green-50 px-4 py-2">Acknowledged ({stats.acknowledged?.length})</p>
              <div className="divide-y divide-gray-50">
                {stats.acknowledged?.map(e => (
                  <div key={e._id} className="px-4 py-2.5 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0"/>
                    <div><p className="text-sm font-medium text-gray-900">{e.firstName} {e.lastName}</p><p className="text-xs text-gray-400">{e.employeeId} · {e.department}</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-red-600 bg-red-50 px-4 py-2">Pending ({stats.pending?.length})</p>
              <div className="divide-y divide-gray-50">
                {stats.pending?.map(e => (
                  <div key={e._id} className="px-4 py-2.5 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-yellow-500 flex-shrink-0"/>
                    <div><p className="text-sm font-medium text-gray-900">{e.firstName} {e.lastName}</p><p className="text-xs text-gray-400">{e.employeeId} · {e.department}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
              <h2 className="font-semibold text-gray-900">{editId?'Edit Policy':'Create Policy'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Policy title"/></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {CATEGORIES.map(c=><option key={c} value={c}>{CAT_LABELS[c]}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                  <select value={form.assignmentType} onChange={e=>setForm(f=>({...f, assignmentType:e.target.value, targetDepartments:[]}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="all">All Employees</option>
                    <option value="department">By Department</option>
                  </select></div>
              </div>
              {form.assignmentType === 'department' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Departments <span className="text-gray-400 font-normal">({form.targetDepartments?.length || 0} selected)</span>
                  </label>
                  {departments.length === 0 ? (
                    <p className="text-xs text-gray-400 italic py-1">No departments configured in org settings.</p>
                  ) : (
                    <div className="border border-gray-300 rounded-lg p-2 max-h-36 overflow-y-auto bg-white space-y-0.5">
                      {departments.map(d => {
                        const name = d.name || d;
                        const checked = form.targetDepartments?.includes(name);
                        return (
                          <label key={name} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!checked}
                              onChange={() => setForm(f => ({
                                ...f,
                                targetDepartments: checked
                                  ? f.targetDepartments.filter(x => x !== name)
                                  : [...(f.targetDepartments || []), name]
                              }))}
                              className="w-3.5 h-3.5 text-blue-600 rounded"
                            />
                            <span className="text-sm text-gray-800">{name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  {form.targetDepartments?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {form.targetDepartments.map(d => (
                        <span key={d} className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full flex items-center gap-1">
                          {d}
                          <button type="button" onClick={() => setForm(f => ({ ...f, targetDepartments: f.targetDepartments.filter(x => x !== d) }))}><X className="w-3 h-3"/></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input type="date" value={form.dueDate} onChange={e=>setForm(f=>({...f,dueDate:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Upload File (PDF/Image/Word)</label>
                <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                  <Upload className="w-4 h-4 text-gray-400"/>
                  <span className="text-sm text-gray-500">{file ? file.name : form.fileName || 'Choose file'}</span>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={e=>setFile(e.target.files[0]||null)}/>
                </label></div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">{saving?'Saving...':'Save Policy'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PoliciesPage;
